import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import useAuthState from "../../../hooks/useAuthState";
import useTutorialsState from "../../../hooks/useTutorialsState";
import { useMixpanel } from "react-mixpanel-browser";
import useMaskedInputState from "../../../hooks/useMaskedInputState";
import useInputState from "../../../hooks/useInputState";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";

import Auth0 from "../../../helpers/Auth0";
import { isEmail } from "validator";
import { validPassword } from "../../../helpers/password";

import { UpdateAccountInfo } from "./mutations";
import { GetProfileInfo } from "./queries";
import useTagAccount from "../../../hooks/useTagAccount";

const useProfileInfo = () => {
  const [firstName, setFirstName] = useMaskedInputState("",{maxLength:255});
  const [lastName, setLastName] = useMaskedInputState("",{maxLength:255});
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useMaskedInputState("",{maxLength:255});
  const [password, setPassword] = useMaskedInputState("",{maxLength:255});

  const [username, setUsername] = useMaskedInputState("", {
    regex: /^[a-zA-Z0-9@^$.\-#+'~_\b]+$/,
    maxLength: 30,
    convertToLowerCase: true
  });

  const [mutationLoading, setLoading] = useState(false);
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const [error, setError] = useState();
  const [isFormValid, setIsFormValid] = useState(true);
  const { login } = useAuthState();
  const history = useHistory();
  const location = useLocation();
  const mixpanel = useMixpanel();

  const { tagAccount } = useTagAccount();
  const [updateAccountInfo] = useMutation(UpdateAccountInfo, {
    refetchQueries: [{ query: GetProfileInfo }]
  });

  const { data, queryLoading } = useQuery(GetProfileInfo, {
    onError: (err) => {
      setError(getError());
    }
  });

  const pocName =
    data && data.accounts && data.accounts[0] && data.accounts[0].pocName;
  const savedEmail =
    data && data.accounts && data.accounts[0] && data.accounts[0].email;
  const savedUsername =
    data && data.accounts && data.accounts[0] && data.accounts[0].username;

  const savedFirstName = !!pocName
    ? pocName
        .split(" ")
        .slice(0, -1)
        .join(" ")
    : null;
  const savedLastName = !!pocName
    ? pocName
        .split(" ")
        .slice(-1)
        .join(" ")
    : null;

  useEffect(() => {
    setFirstName(savedFirstName);
    setLastName(savedLastName);
  }, [savedFirstName, savedLastName]);

  const pocNameValidation = !!firstName && !!lastName;
  const validForm =
    pocNameValidation &&
    username &&
    !isEmail(username) &&
    email &&
    isEmail(email) &&
    password &&
    validPassword(password);

  const createAccount = async (e) => {
    e.preventDefault();

    setIsFormValid(validForm);

    if (!!savedUsername) {
      await updateUserProfile();
    } else {
      await registerUser();
    }

    const hasCompletedOnboardingStep = getHasCompletedTutorial(
      "Brand Dashboard Personal Info"
    );

    if (!hasCompletedOnboardingStep) {
      await completeTutorial({
        variables: { name: "Brand Dashboard Personal Info", version: 1 }
      });
    }
  };

  const updateUserProfile = async () => {
    if (pocNameValidation) {
      setLoading(true);
      try {
        const hasCompletedOnboardingStep = getHasCompletedTutorial(
          "Brand Dashboard Personal Info"
        );

        if (!hasCompletedOnboardingStep) {
          await completeTutorial({
            variables: { name: "Brand Dashboard Personal Info", version: 1 }
          });
        }
        if (
          (!!firstName && firstName !== savedFirstName) ||
          (!!lastName && lastName !== savedLastName)
        ) {
          await updateAccountInfo({
            variables: {
              account: { pocName: firstName + " " + lastName }
            }
          });
        }
        history.push({
          pathname: "/register/organization",
          search: location.search
        });
      } catch (err) {
        setError(getError());
      }
    }

    setLoading(false);
  };

  const registerUser = async () => {
    if (validForm) {
      setLoading(true);

      try {
        const auth0 = new Auth0();
        const pocFullName = firstName + " " + lastName;
        await auth0.createAccount({
          name: pocFullName,
          username,
          email,
          password
        });
        await login(email, password, true);
        mixpanel.track("Registered");

        await updateAccountInfo({
          variables: {
            account: { pocName: pocFullName }
          }
        });

        await tagAccount("Brand");

        setLoading(false);
        history.push("/register/organization");
      } catch (err) {
        setLoading(false);
        if (err.code === "missing_property") {
          setError({
            title: "We couldn't create your account at this time.",
            message: "Email address cannot be used as username."
          });
        } else if (
          err.code === "user_exists" ||
          err.code === "username_exists"
        ) {
          setError({
            title: "A user with that email address or username already exists.",
            code: err.code,
            message: "If it’s you, you can login."
          });
        } else {
          setError({
            title: "We couldn't create your account at this time.",
            message:
              "We apologize for the inconvenience. Please try again later."
          });
          throw err;
        }
      }
    }
  };

  return {
    savedEmail,
    savedFirstName,
    savedLastName,
    savedUsername,
    firstName,
    lastName,
    username,
    email,
    password,
    error,
    isFormValid: savedUsername ? pocNameValidation : isFormValid,
    disabled: savedUsername ? !pocNameValidation : !isFormValid && !validForm,
    mutationLoading,
    setFirstName,
    setLastName,
    onUsernameChange: setUsername,
    setEmail,
    setPassword,
    showPassword,
    setShowPassword,
    createAccount,
    invalidPassword: !savedEmail && !!password && !validPassword(password),
    invalidUsername: !savedUsername && !!username && isEmail(username),
    invalidEmail: !savedEmail && !!email && !isEmail(email)
  };
};

const getError = () => {
  return {
    message: (
      <span>
        Something went wrong! Try again or contact{" "}
        {
          <a
            href="https://help.videosquirrel.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            support
          </a>
        }{" "}
        if the problem persists.
      </span>
    )
  };
};

export default useProfileInfo;
